/* eslint-disable @typescript-eslint/no-explicit-any */

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Calendar, MapPin, Share2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

import AddToCalendarButton from "@/components/AddToCalendarButton";
import AdventureHero from "@/components/adventures/AdventureHero";
import EventChat from "@/components/EventChat";
import KindredShareSheet from "@/components/KindredShareSheet";
import { MemberPageScaffold, MemberPill } from "@/components/MemberScaffold";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { buildAdventureDescriptionFromEvent, buildEventAdventureMarker } from "@/lib/adventure-helpers";
import { buildKindredExternalShareText, buildKindredWebUrl } from "@/lib/kindredShare";
import { getAdventureCoverFromEventImage } from "../../shared/activities";
import {
  buildGoogleMapsDirectionsUrl,
  buildGoogleMapsSearchUrl,
  hasMapTarget,
} from "../../shared/location";
import { normalizeEventRecord } from "../../shared/activities";
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppErrorState,
  AppLoadingState,
  AppSection,
  AppSectionHeader,
  AppText,
} from "../../ui-foundation";

type MetaRowProps = {
  icon: LucideIcon;
  children: ReactNode;
};

function MetaRow({ icon: Icon, children }: MetaRowProps) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Icon size={16} className="shrink-0 text-primary" />
      <span>{children}</span>
    </div>
  );
}

const EventDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [myRsvp, setMyRsvp] = useState<string | null>(null);
  const [rsvpCounts, setRsvpCounts] = useState({ interested: 0, going: 0 });
  const [goingProfiles, setGoingProfiles] = useState<any[]>([]);
  const [chatRoom, setChatRoom] = useState<any>(null);
  const [isChatMember, setIsChatMember] = useState(false);
  const [changingRsvp, setChangingRsvp] = useState(false);
  const [linkedAdventure, setLinkedAdventure] = useState<{
    id: string;
    title: string;
    interested: number;
    going: number;
  } | null>(null);
  const [sharingToAdventure, setSharingToAdventure] = useState(false);

  const fetchData = useCallback(async () => {
    if (!id || !user) return;

    const marker = buildEventAdventureMarker(id);
    const [{ data }, { data: rsvps }, { data: room }, { data: linked }] = await Promise.all([
      supabase.from("events").select("*").eq("id", id).single(),
      supabase.from("event_rsvps" as any).select("*").eq("event_id", id),
      supabase.from("event_chat_rooms" as any).select("*").eq("event_id", id).maybeSingle(),
      supabase.from("adventures").select("id, title").ilike("description", `%${marker}%`).maybeSingle(),
    ]);

    setEvent(data ? normalizeEventRecord(data as any) : null);

    const rsvpList = (rsvps as any[]) || [];
    const myRecord = rsvpList.find((record: any) => record.user_id === user.id);
    setMyRsvp(myRecord ? (myRecord as any).status : null);
    setRsvpCounts({
      interested: rsvpList.filter((record: any) => record.status === "interested").length,
      going: rsvpList.filter((record: any) => record.status === "going").length,
    });

    const goingIds = rsvpList
      .filter((record: any) => record.status === "going")
      .map((record: any) => record.user_id)
      .slice(0, 5);

    if (goingIds.length > 0) {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, display_name, avatar_url")
        .in("id", goingIds);
      setGoingProfiles(profiles || []);
    } else {
      setGoingProfiles([]);
    }

    if (room) {
      setChatRoom(room);
      const { data: membership } = await supabase
        .from("event_chat_members" as any)
        .select("id")
        .eq("room_id", (room as any).id)
        .eq("user_id", user.id)
        .maybeSingle();
      setIsChatMember(Boolean(membership));
    } else {
      setChatRoom(null);
      setIsChatMember(false);
    }

    if (linked?.id) {
      const { data: members } = await supabase
        .from("adventure_members")
        .select("status")
        .eq("adventure_id", linked.id);
      const memberList = members || [];
      setLinkedAdventure({
        id: linked.id,
        title: linked.title,
        interested: memberList.filter((member: any) => member.status === "interested").length,
        going: memberList.filter((member: any) => member.status === "joined").length,
      });
    } else {
      setLinkedAdventure(null);
    }

    setLoading(false);
  }, [id, user]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handleRsvp = async (status: string) => {
    if (!user || !id) return;

    if (myRsvp === status) {
      await supabase.from("event_rsvps" as any).delete().eq("event_id", id).eq("user_id", user.id);
      setMyRsvp(null);
    } else if (myRsvp) {
      await supabase.from("event_rsvps" as any).update({ status } as any).eq("event_id", id).eq("user_id", user.id);
      setMyRsvp(status);
    } else {
      await supabase.from("event_rsvps" as any).insert({ event_id: id, user_id: user.id, status } as any);
      setMyRsvp(status);
    }

    if (status === "interested" || status === "going") {
      let roomId = chatRoom?.id;
      if (!roomId) {
        const { data: newRoom } = await supabase
          .from("event_chat_rooms" as any)
          .insert({ event_id: id } as any)
          .select()
          .single();
        if (newRoom) {
          roomId = (newRoom as any).id;
          setChatRoom(newRoom);
        }
      }

      if (roomId && !isChatMember) {
        await supabase.from("event_chat_members" as any).insert({ room_id: roomId, user_id: user.id } as any);
        setIsChatMember(true);
      }
    }

    setChangingRsvp(false);
    await fetchData();
  };

  const openOrCreateAdventure = async () => {
    if (!user || !event || !id || sharingToAdventure) return;

    if (linkedAdventure?.id) {
      navigate(`/adventures/${linkedAdventure.id}`);
      return;
    }

    setSharingToAdventure(true);
    const marker = buildEventAdventureMarker(id);
    const { data: existing } = await supabase
      .from("adventures")
      .select("id, title")
      .ilike("description", `%${marker}%`)
      .maybeSingle();

    if (existing?.id) {
      setSharingToAdventure(false);
      navigate(`/adventures/${existing.id}`);
      return;
    }

    const { data: adventure, error } = await supabase
      .from("adventures")
      .insert({
        user_id: user.id,
        type: "social",
        title: event.title,
        description: buildAdventureDescriptionFromEvent(event.description, id),
        image_url: getAdventureCoverFromEventImage(event.image_url),
        location: event.location,
        venue_url: event.url ?? null,
        date_start: String(event.event_date).split("T")[0],
        date_end: null,
        max_group_size: 8,
      })
      .select("id, title")
      .single();

    if (error || !adventure?.id) {
      setSharingToAdventure(false);
      toast.error(error?.message || "Could not start the group plan");
      return;
    }

    await supabase.from("adventure_members").insert({
      adventure_id: adventure.id,
      user_id: user.id,
      status: "joined",
    });

    setSharingToAdventure(false);
    toast.success("Group plan opened in Activities");
    navigate(`/adventures/${adventure.id}`);
  };

  if (loading) {
    return (
      <MemberPageScaffold
        title="What’s On"
        subtitle="Loading the event details."
        fallbackPath="/activities?section=whats-on"
      >
        <AppLoadingState title="Loading event" body="Pulling in the details, RSVPs, and chat." />
      </MemberPageScaffold>
    );
  }

  if (!event) {
    return (
      <MemberPageScaffold
        title="What’s On"
        subtitle="This event is no longer available."
        fallbackPath="/activities?section=whats-on"
      >
        <AppErrorState
          title="Event not found"
          body="It may have been removed or the link may be out of date."
          action={
            <AppButton
              type="button"
              tone="outline"
              onPress={() => navigate("/activities?section=whats-on")}
            >
              Back to What&apos;s On
            </AppButton>
          }
        />
      </MemberPageScaffold>
    );
  }

  const sharePayload = {
    version: 1 as const,
    type: "event" as const,
    targetId: event.id,
    title: event.title,
    subtitle: `${format(new Date(event.event_date), "EEE d MMM yyyy, h:mma")} · ${event.location}`,
    imageUrl: event.image_url ?? null,
    route: `/whats-on/${event.id}`,
    externalUrl: buildKindredWebUrl(`/whats-on/${event.id}`),
    accessScope: "public" as const,
  };

  const handleExternalShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: sharePayload.title,
          text: buildKindredExternalShareText(sharePayload),
          url: sharePayload.externalUrl,
        });
      } catch {
        return;
      }
      return;
    }

    if (navigator.clipboard) {
      await navigator.clipboard.writeText(sharePayload.externalUrl);
      toast.success("Event link copied");
      return;
    }

    window.prompt("Copy this link", sharePayload.externalUrl);
  };

  const eventSubtitle = `${format(new Date(event.event_date), "EEEE d MMMM yyyy, h:mma")} · ${event.location}`;
  const canOpenMaps = hasMapTarget({
    title: event.title,
    location: event.location,
    coordinates: event.coordinates ?? null,
  });
  const mapsSearchUrl = canOpenMaps
    ? buildGoogleMapsSearchUrl({
        title: event.title,
        location: event.location,
        coordinates: event.coordinates ?? null,
      })
    : null;
  const mapsDirectionsUrl = canOpenMaps
    ? buildGoogleMapsDirectionsUrl({
        title: event.title,
        location: event.location,
        coordinates: event.coordinates ?? null,
      })
    : null;

  return (
    <MemberPageScaffold
      title={event.title}
      subtitle={eventSubtitle}
      fallbackPath="/activities?section=whats-on"
      titleLines={2}
      action={<KindredShareSheet payload={sharePayload} triggerContent={<Share2 size={16} />} iconOnly />}
    >
      <AdventureHero
        imageUrl={event.image_url}
        storageBucket="event-photos"
        badgeLabel="What’s On"
        className="flex h-48 items-end p-4"
      />

      <AppCard>
        <AppCardBody gap="$md">
          <AppSection>
            <MetaRow icon={Calendar}>{format(new Date(event.event_date), "EEEE d MMMM yyyy, h:mma")}</MetaRow>
            <MetaRow icon={MapPin}>{event.location}</MetaRow>
            <div className="flex flex-wrap gap-2">
              {event.is_free ? <MemberPill tone="success">Free</MemberPill> : null}
              {!event.is_free && event.cost ? <MemberPill tone="neutral">{event.cost}</MemberPill> : null}
              {event.source_name ? <MemberPill tone="secondary">Source · {event.source_name}</MemberPill> : null}
            </div>
          </AppSection>

          <AppText lineHeight="$md">{event.description}</AppText>

          <AppButtonRow>
            <AddToCalendarButton
              title={event.title}
              description={event.description}
              location={event.location}
              startDate={new Date(event.event_date)}
              endDate={new Date(new Date(event.event_date).getTime() + 2 * 60 * 60 * 1000)}
            />
            {mapsSearchUrl ? (
              <a href={mapsSearchUrl} target="_blank" rel="noopener noreferrer">
                <AppButton type="button" tone="outline">
                  Open in Maps
                </AppButton>
              </a>
            ) : null}
            {mapsDirectionsUrl ? (
              <a href={mapsDirectionsUrl} target="_blank" rel="noopener noreferrer">
                <AppButton type="button" tone="outline">
                  Get directions
                </AppButton>
              </a>
            ) : null}
            <AppButton type="button" tone="outline" onPress={() => void handleExternalShare()}>
              Share link
            </AppButton>
            {event.url ? (
              <a href={event.url} target="_blank" rel="noopener noreferrer">
                <AppButton type="button">Find out more</AppButton>
              </a>
            ) : null}
          </AppButtonRow>
        </AppCardBody>
      </AppCard>

      <AppCard>
        <AppCardBody gap="$md">
          <AppSectionHeader
            title="RSVP"
            subtitle={`${rsvpCounts.interested} interested · ${rsvpCounts.going} going`}
          />

          {myRsvp === "going" && !changingRsvp ? (
            <AppSection>
              <AppButton type="button" disabled width="100%">
                You&apos;re going ✓
              </AppButton>
              <AppButton type="button" tone="outline" onPress={() => setChangingRsvp(true)} width="100%">
                Change RSVP
              </AppButton>
            </AppSection>
          ) : myRsvp === "interested" && !changingRsvp ? (
            <AppSection>
              <AppButton type="button" tone="outline" disabled width="100%">
                Interested ✓
              </AppButton>
              <AppButton type="button" onPress={() => void handleRsvp("going")} width="100%">
                I&apos;m going ✓
              </AppButton>
              <AppButton type="button" tone="outline" onPress={() => setChangingRsvp(true)} width="100%">
                Change RSVP
              </AppButton>
            </AppSection>
          ) : (
            <AppSection>
              <AppButton type="button" onPress={() => void handleRsvp("going")} width="100%">
                I&apos;m going ✓
              </AppButton>
              <AppButtonRow justifyContent="center">
                <AppButton type="button" tone="outline" onPress={() => void handleRsvp("interested")}>
                  Interested
                </AppButton>
                <AppButton type="button" tone="outline" onPress={() => void handleRsvp("not_going")}>
                  Not going
                </AppButton>
              </AppButtonRow>
            </AppSection>
          )}

          {goingProfiles.length > 0 ? (
            <div className="flex items-center justify-center gap-1">
              {goingProfiles.map((profile: any) => (
                <Avatar key={profile.id} className="h-7 w-7 -ml-1 first:ml-0 border-2 border-background">
                  {profile.avatar_url ? <AvatarImage src={profile.avatar_url} /> : null}
                  <AvatarFallback className="bg-primary/20 text-primary text-[10px]">
                    {(profile.display_name ?? "K")[0]}
                  </AvatarFallback>
                </Avatar>
              ))}
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm" marginLeft="$sm">
                going
              </AppText>
            </div>
          ) : null}
        </AppCardBody>
      </AppCard>

      <AppCard>
        <AppCardBody gap="$md">
          <AppSectionHeader
            title="Want to go together?"
            subtitle="Turn this event into a Kindred group plan so other members can join it through Activities."
            action={
              <AppButton
                type="button"
                size="sm"
                onPress={() => void openOrCreateAdventure()}
                disabled={sharingToAdventure}
              >
                {linkedAdventure ? "Open group plan" : sharingToAdventure ? "Starting..." : "Start group plan"}
              </AppButton>
            }
          />

          {linkedAdventure ? (
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
              {linkedAdventure.interested} interested · {linkedAdventure.going} going in the linked activity
            </AppText>
          ) : null}
        </AppCardBody>
      </AppCard>

      {chatRoom && isChatMember ? (
        <AppCard>
          <AppCardBody>
            <AppSectionHeader
              title="Event chat"
              subtitle="You’re in the room, so you can read and reply here."
            />
            <EventChat
              roomId={(chatRoom as any).id}
              eventId={event.id}
              eventTitle={event.title}
            />
          </AppCardBody>
        </AppCard>
      ) : chatRoom ? (
        <AppCard>
          <AppCardBody>
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
              {rsvpCounts.interested + rsvpCounts.going} people are already chatting about this event. Join by
              choosing Interested or Going.
            </AppText>
          </AppCardBody>
        </AppCard>
      ) : null}
    </MemberPageScaffold>
  );
};

export default EventDetail;
